import { getCsrfToken } from '@/util';

/**
 * NEN 7513 events only the browser sees: copying patient data and printing the
 * page. Elements showing an invite's data carry `data-audit-invite="<uuid>"`
 * (rendered for signed-in users only); the listeners report which of those a
 * copy or print touched, and the server logs them per invite.
 */
export type BrowserAuditEvent = 'copy' | 'copy_button' | 'copy_link' | 'print';

export const AUDIT_INVITE_ATTRIBUTE = 'data-audit-invite';

export function reportBrowserAuditEvent(event: BrowserAuditEvent, uuids: string[]): void {
    const invites = [...new Set(uuids.filter((uuid) => uuid !== ''))];
    if (invites.length === 0) {
        return;
    }

    // keepalive: a print or copy is often followed by leaving the page.
    void fetch(route('audit.browser-events'), {
        method: 'POST',
        keepalive: true,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCsrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ event, invites }),
    }).catch(() => undefined);
}

function markedElements(root: ParentNode): HTMLElement[] {
    return [...root.querySelectorAll<HTMLElement>(`[${AUDIT_INVITE_ATTRIBUTE}]`)];
}

function uuidOf(element: Element | null): string | null {
    return element?.closest(`[${AUDIT_INVITE_ATTRIBUTE}]`)?.getAttribute(AUDIT_INVITE_ATTRIBUTE) ?? null;
}

/**
 * The invites whose data a copy took: text selected inside an input, or a page
 * selection that overlaps marked elements.
 */
export function invitesInCopy(selection: Selection | null, target: EventTarget | null, root: ParentNode = document): string[] {
    if ((target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) && target.selectionStart !== target.selectionEnd) {
        const uuid = uuidOf(target);
        return uuid ? [uuid] : [];
    }

    if (!selection || selection.isCollapsed || selection.toString().trim() === '') {
        return [];
    }

    const ranges = Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index));
    return markedElements(root)
        .filter((element) => ranges.some((range) => range.intersectsNode(element)))
        .map((element) => element.getAttribute(AUDIT_INVITE_ATTRIBUTE) ?? '');
}

/**
 * The invites a print of the page contains: the marked elements that are shown
 * (a closed modal or a hidden tab is not printed).
 */
export function invitesOnPage(root: ParentNode = document): string[] {
    return markedElements(root)
        .filter((element) => (typeof element.checkVisibility === 'function' ? element.checkVisibility() : true))
        .map((element) => element.getAttribute(AUDIT_INVITE_ATTRIBUTE) ?? '');
}

export function installBrowserAuditListeners(): () => void {
    const onCopy = (event: ClipboardEvent) => reportBrowserAuditEvent('copy', invitesInCopy(document.getSelection(), event.target));
    const onPrint = () => reportBrowserAuditEvent('print', invitesOnPage());

    document.addEventListener('copy', onCopy);
    window.addEventListener('beforeprint', onPrint);

    return () => {
        document.removeEventListener('copy', onCopy);
        window.removeEventListener('beforeprint', onPrint);
    };
}
